import { resolveFormTenantId } from "../../utils/tenantContext";
import { zodResolver } from "@hookform/resolvers/zod";
import { getStandardizedSignatures } from '../../utils/formHelpers';
import { useEffect, useState } from "react";
import { FormProvider, useFieldArray, useForm } from "react-hook-form";
import { toast } from "react-hot-toast";
import { useApplicationFormLogic } from '../../hooks/useApplicationFormLogic';
import { request } from "../../utils/api";
import { getLocalSystemDate, parseIsoDateToMDYUS, toIsoDate } from "../../utils/dateUtils";
import { mergeFormDataWithStatusExtras, applyIsoDatesToFields } from "../../utils/applicationFormPayload";
import EvaluationFormHeader from "../EvaluationNew/components/EvaluationFormHeader";
import EvaluationInput from "../EvaluationNew/components/EvaluationInput";
import InputDatePicker from "../EvaluationNew/components/InputDatePicker";
import { orientationChecklistSchema } from "./application.validation";
import ApplicationFormFooter from './ApplicationFormFooter';
import CloseButton from "../../components/Application/CloseButton";
import CorrectionRequestAlert from "../../components/Application/CorrectionRequestAlert";

export const CHECKLIST_ITEMS = [
  { key: "1", text: "Tour of office/introduction of organization personnel" },
  { key: "2", text: "Receipt of Contractor's Handbook" },
  { key: "3", text: "Completion of contracting forms" },
  { key: "4", text: "Name and Photo Identification" },
  {
    key: "5",
    text: "Orientation content for all contractors",
    subItems: [
      "A. General orientation to organization, including Mission, Philosophy, Vision",
      "B. Review of organizational chart",
      "C. Contracting Process",
      "D. Care and services provided by the organization",
      "E. Organization safety review",
      "F. Infection prevention and control within the organization including the influenza vaccination program",
      "G. Performance improvement process",
      "H. Confidentiality of organization and patient information/HIPAA",
      "I. Type of care delivered in the patient's environment",
      "J. Available community resources",
      "K. Equipment management",
      "L. Home safety issues",
      "M. Storage, handling and access to supplies, medical gases and drugs",
      "N. Identification, handling and disposal of hazardous or infectious materials",
      "O. Infection control practices (specific to the home setting)",
      "P. Actions in unsafe situations",
      "Q. Other patient care and service responsibilities",
      "R. Specific tests to be performed by organization personnel (i.e., venipuncture, HGM)",
      "S. Advance directives",
      "T. Appropriate policies and procedures",
      "U. Screening for abuse and neglect",
      "V. Guideline for appropriate referrals, including timeliness",
      "W. Emergency preparedness within the organization and home care setting",
      "X. Information regarding services provided by other members of the organization personnel",
      "Y. Death and dying",
      "Z. Patient Rights and Responsibilities",
      "AA. Ethical issues",
      "BB. Cultural diversity and sensitivity",
      "CC. Corporate Compliance Program",
    ],
  },
];

const OrientationChecklist = ({ positionName, onClose }) => {
  const [checklistId, setChecklistId] = useState(null);

  const methods = useForm({
    resolver: zodResolver(orientationChecklistSchema),
    defaultValues: {
      tenantId: undefined,
      userId: undefined,
      contractorName: "",
      date: parseIsoDateToMDYUS(getLocalSystemDate()),
      applicantSignature: "",
      applicantSignedBy: "",
      applicantSignedAt: "",
      reviewedBySignature: "",
      reviewedBySignedAt: "",
      reviewedBySignedBy: "",
      positionAppliedFor: positionName || "",
      items: CHECKLIST_ITEMS.map((item, index) => ({
        itemNumber: index + 1,
        itemDescription: item.text,
        contractorInitials: "",
        dateOfOrientation: parseIsoDateToMDYUS(getLocalSystemDate())
      }))
    },
    mode: "onChange"
  });

  const { handleSubmit, setValue, watch, control, reset } = methods;
  const { fields } = useFieldArray({
    control,
    name: "items"
  });

  const [currentStatus, setCurrentStatus] = useState("");

  const onSubmit = async (data, flag = 'send_to_office', additionalData = {}) => {
    const actualFlag = typeof flag === 'string' ? flag : 'send_to_office';
    try {
      const merged = mergeFormDataWithStatusExtras(data, additionalData);
      const payload = {
        ...merged,
        ...applyIsoDatesToFields(merged, ["date", "applicantSignedAt", "reviewedBySignedAt"]),
        items: (merged.items || []).map((item) => ({
          ...item,
          dateOfOrientation: toIsoDate(item.dateOfOrientation),
        })),
        flag: actualFlag,
      };

      // Handle To Be Corrected comments
      const finalComments = additionalData?.comments || data?.comments || methods.getValues("comments");
      if (finalComments) {
        payload.comments = finalComments;
      }

      if (checklistId) {
        payload.checklistId = checklistId;
      }

      let response;
      if (checklistId) {
        response = await request(`/orientation-checklists/${checklistId}`, {
          method: 'PUT',
          body: JSON.stringify(payload)
        });
      } else {
        response = await request('/orientation-checklists', {
          method: 'POST',
          body: JSON.stringify(payload)
        });
      }

      if (response.status === 1) {
        toast.success(response.message || "Orientation Checklist processed successfully!");
        closeForm();
      } else {
        toast.error(response.message || "Failed to process form!");
      }
    } catch (error) {
      console.error("Error processing form:", error);
      toast.error(`Failed to process form: ${error.message}`);
    }
  };

  const {
    handleStatusAction,
    handleSave,
    targetUserId,
    user: currentUserData,
    isAdmin,
    isReadOnly,
    closeForm,
    actualFromId,
    isSignatureModalOpen,
    openSignatureModal,
    closeSignatureModal,
    saveSignature,
    signerName
  } = useApplicationFormLogic({
    methods,
    applicantNameField: "contractorName",
    applicantSignatureField: "applicantSignature",
    applicantSignatureDateField: "applicantSignedAt",
    adminSignatureField: "reviewedBySignature",
    adminSignatureDateField: "reviewedBySignedAt",
    onClose,
    onSubmit,
    currentStatus
  });

  useEffect(() => {
    if (positionName) {
      setValue("positionAppliedFor", positionName);
    }
  }, [positionName, setValue]);

  useEffect(() => {
    const fetchOrientationChecklist = async () => {
      // Must wait for currentUserData
      if (!currentUserData) return;
      if (isAdmin && !targetUserId && !checklistId) return;

      try {
        if (!actualFromId && !targetUserId && !checklistId) {
          if (currentUserData && !isAdmin) {
            reset({
              tenantId: resolveFormTenantId(currentUserData?.tenantId),
              userId: currentUserData.id,
              contractorName: `${currentUserData.firstName || ''} ${currentUserData.lastName || ''}`.trim(),
              date: parseIsoDateToMDYUS(getLocalSystemDate()),
              positionAppliedFor: positionName || "",
              items: CHECKLIST_ITEMS.map((item, index) => ({
                itemNumber: index + 1,
                itemDescription: item.text,
                contractorInitials: "",
                dateOfOrientation: parseIsoDateToMDYUS(getLocalSystemDate())
              }))
            });
          }
          return;
        }

        let response;
        if (isAdmin) {
          if (actualFromId) {
            response = await request(`/orientation-checklists/${actualFromId}?userId=${targetUserId}`);
          } else {
            response = await request(`/orientation-checklists?userId=${targetUserId}`);
          }
        } else {
          response = await request(`/orientation-checklists/${actualFromId || targetUserId}`);
        }

        let data = null;
        if (response.status === 1) {
          if (Array.isArray(response.result)) {
            if (response.result.length > 0) {
              data = response.result[0];
            }
          } else if (response.result?.checklist) {
            data = response.result.checklist;
          } else if (response.result?.items && Array.isArray(response.result.items)) {
            if (response.result.items.length > 0) {
              data = response.result.items.find(item => item.userId === Number.parseInt(targetUserId, 10));
            }
          }
        }

        if (data) {
          setChecklistId(data.id);
          setCurrentStatus(data.status || "");
          // Using isAdmin from hook
          const formattedItems = (data.items || []).map(item => ({
            ...item,
            dateOfOrientation: item.dateOfOrientation ? item.dateOfOrientation.split('T')[0] : ""
          }));

          const mergedItems = CHECKLIST_ITEMS.map((defaultItem, index) => {
            const itemNumber = index + 1;
            const existing = formattedItems.find(i => i.itemNumber === itemNumber);

            return {
              itemNumber: itemNumber,
              itemDescription: defaultItem.text,
              contractorInitials: existing ? existing.contractorInitials : "",
              dateOfOrientation: existing ? existing.dateOfOrientation : getLocalSystemDate()
            };
          });

          reset({
            ...data,
            contractorName: data.contractorName || data.applicantName || "",
            date: parseIsoDateToMDYUS(data.date || getLocalSystemDate()),
            ...getStandardizedSignatures(data, isAdmin),
            applicantSignedAt: parseIsoDateToMDYUS(getStandardizedSignatures(data, isAdmin).applicantSignedAt || getLocalSystemDate()),
            reviewedBySignedAt: parseIsoDateToMDYUS(getStandardizedSignatures(data, isAdmin).reviewedBySignedAt || ""),
            items: mergedItems
          });
        }
      } catch (error) {
        console.error("Error fetching orientation checklist:", error);
      }
    };

    fetchOrientationChecklist();
  }, [targetUserId, reset, currentUserData, isAdmin, checklistId, actualFromId, positionName]);



  const handleCustomSignatureApply = () => {
    const result = saveSignature();
    if (result && result.formattedSignature) {
      const isAdm = result.signatureType === 'admin';
      const targetField = isAdm ? "reviewedBySignature" : "applicantSignature";
      const dateField = isAdm ? "reviewedBySignedAt" : "applicantSignedAt";

      setValue(targetField, result.formattedSignature, { shouldValidate: true });
      setValue(dateField, new Date().toISOString(), { shouldValidate: true });

      if (isAdm) {
        setValue("reviewedBySignedBy", result.signerName);
      } else {
        setValue("applicantSignedBy", result.signerName);
      }

      closeSignatureModal();
    }
  };


  return (
    <>
      <CloseButton onClose={closeForm} />
      <div className="max-w-5xl mx-auto bg-white shadow-xl rounded-2xl my-8">
        <EvaluationFormHeader title="Orientation Checklist" />

        <div className="p-8">
          <FormProvider {...methods}>
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
              <CorrectionRequestAlert status={currentStatus} comments={methods.watch("comments")} />
              <fieldset disabled={isReadOnly} className="space-y-8">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <EvaluationInput
                      name="contractorName"
                      label="Contractor:"
                      placeholder="Contractor name"
                    />
                  </div>
                  <div>
                    <InputDatePicker
                      formatSeparator="/"
                      name="date"
                      label="Date:"
                      max={getLocalSystemDate()}
                    />
                  </div>
                </div>

                <div className="overflow-hidden rounded-xl border border-gray-200 shadow-sm">
                  <table className="w-full border-collapse text-sm">
                    <thead>
                      <tr className="bg-gray-50 text-gray-700">
                        <th className="border-b border-gray-200 p-4 text-left font-bold uppercase tracking-wider w-full">
                          CHECKLIST
                        </th>
                        <th className="border-b border-gray-200 p-4 text-center font-bold uppercase tracking-wider w-40 whitespace-nowrap">
                          CONTRACTOR INITIALS <span className="text-red-500">*</span>
                        </th>
                        <th className="border-b border-gray-200 p-4 text-center font-bold uppercase tracking-wider w-48 whitespace-nowrap">
                          DATE OF ORIENTATION <span className="text-red-500">*</span>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {fields.map((field, index) => {
                        const item = CHECKLIST_ITEMS[index];
                        return (
                          <tr
                            key={field.id}
                            className="border-b border-gray-100 last:border-0 hover:bg-gray-50 transition-colors"
                          >
                            <td className="p-4 align-top">
                              <div className="flex gap-3">
                                <div className="font-bold text-gray-900 min-w-[24px]">
                                  {item.key}.
                                </div>
                                <div className="flex-1 text-gray-800">
                                  {item.text ===
                                    "Orientation content for all contractors" ? (
                                    <div className="text-sm leading-relaxed">
                                      <div className="font-medium mb-2">
                                        The orientation content for all contractors
                                        will include the following as applicable and
                                        appropriate to the care and service
                                        provided:
                                      </div>
                                      <div className="pl-4 space-y-1.5 border-l-2 border-gray-100 ml-1">
                                        {item.subItems?.map((sub, i) => (
                                          <div key={i} className="text-gray-600">
                                            <span className="font-bold text-gray-800">
                                              {sub.split(" ")[0]}
                                            </span>
                                            <span className="ml-1">
                                              {sub.substring(sub.indexOf(" ") + 1)}
                                            </span>
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  ) : (
                                    <span className="font-medium">{item.text}</span>
                                  )}
                                </div>
                              </div>
                            </td>
                            <td className="p-4 align-top text-center bg-gray-50/30">
                              <EvaluationInput
                                name={`items.${index}.contractorInitials`}
                                placeholder="Initials"
                                className="text-center bg-white"
                                isRequired
                              />
                            </td>
                            <td className="p-4 align-top text-center bg-gray-50/30">
                              <InputDatePicker
                                formatSeparator="/"
                                name={`items.${index}.dateOfOrientation`}
                                max={getLocalSystemDate()}
                                inputClassName="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 text-sm bg-white"
                                isRequired
                              />
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </fieldset>

              <ApplicationFormFooter
                page={1}
                setPage={() => { }}
                totalPages={1}
                methods={methods}
                onSave={handleSave}
                onSubmit={(data, status, extra) => onSubmit(data, status, extra)}
                onStatusUpdate={handleStatusAction}
                isSignatureModalOpen={isSignatureModalOpen}
                openSignatureModal={openSignatureModal}
                closeSignatureModal={closeSignatureModal}
                handleSignatureApply={handleCustomSignatureApply}
                signerName={signerName}
                showCertification={false}
                requireSignatures={true}
                showSignatureSections={true}
                applicantName={watch("contractorName") || ""}
                applicantSignature={watch("applicantSignature") || ""}
                applicantSignedAt={watch("applicantSignedAt") || ""}
                reviewedBySignedBy={watch("reviewedBySignedBy") || signerName}
                reviewedBySignature={watch("reviewedBySignature") || ""}
                reviewedBySignedAt={watch("reviewedBySignedAt") || ""}
                submitLabel="Submit & Print"
                isReadOnly={isReadOnly}
                onClose={closeForm}
                currentStatus={currentStatus}
              />

            </form>


          </FormProvider>
        </div>

      </div>
    </>
  );
};

export default OrientationChecklist;
